import type {
  CollectionReference,
  DocumentReference,
  Firestore,
  Query,
} from 'firebase/firestore';
import {collection, doc, getFirestore, query, where} from 'firebase/firestore';
import {z} from 'zod';

import {
  COURSE_COLLECTION,
  CourseDocumentSchema,
  getCourseDocumentPath,
} from '../documents/course';
import {createConverter} from './sdk';

// ============================================================================
// SDK-Specific Schema (client)
// ============================================================================

export const CourseSchema = CourseDocumentSchema.loose();

export type CourseDocument = z.infer<typeof CourseSchema>;

// ============================================================================
// Document Refs
// ============================================================================

const courseConverter = createConverter({
  schema: CourseSchema,
  options: {validateOnRead: false, validateOnWrite: false},
});

export function getCourseDocumentRef({
  courseId,
  firestore = getFirestore(),
}: {
  courseId: string;
  firestore?: Firestore;
}): DocumentReference<CourseDocument> {
  return doc(firestore, getCourseDocumentPath({courseId})).withConverter(
    courseConverter,
  );
}

export function getCourseCollectionRef({
  firestore = getFirestore(),
}: {
  firestore?: Firestore;
}): CollectionReference<CourseDocument> {
  return collection(firestore, COURSE_COLLECTION).withConverter(
    courseConverter,
  );
}

/**
 * Returns a query for all active courses.
 */
export function getActiveCoursesQuery({
  firestore = getFirestore(),
}: {
  firestore?: Firestore;
}): Query<CourseDocument> {
  return query(
    getCourseCollectionRef({firestore}),
    where('isActive', '==', true),
  );
}
